import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Button, ButtonGroup, Grid } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { $generateNodesFromDOM } from '@lexical/html';
import {
    FORMAT_TEXT_COMMAND,
    FORMAT_ELEMENT_COMMAND,
    LexicalEditor, ElementFormatType, LexicalCommand, TextFormatType,
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isRootOrShadowRoot,
    $insertNodes,
    SELECTION_CHANGE_COMMAND,
    COMMAND_PRIORITY_CRITICAL
} from 'lexical';
import {
    $findMatchingParent,
    $getNearestNodeOfType,
    mergeRegister,
} from '@lexical/utils';
import {
    $setBlocksType,
} from '@lexical/selection';
import {
    $isListNode,
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND,
} from '@lexical/list';
import {
    $createHeadingNode,
    $isHeadingNode,
} from '@lexical/rich-text';

interface TextToolbarGroupProp<T> {
    action: LexicalCommand<T>,
    options: {
        icon: IconProp,
        option: T,
        active: boolean
    }[]
}

const BlockFormatValues = ['paragraph', 'bullet', 'number', 'quote', 'heading', 'code', 'h3'] as const;
type BlockFormatType = typeof BlockFormatValues[number];


interface ToolbarFormatSectionProp {
    group: TextToolbarGroupProp<any>,
    editor: LexicalEditor
}

interface ToolbarOptionSectionProp {
    icon: IconProp,
    event: VoidFunction
}

interface ToolbarBlockOptionProp extends ToolbarOptionSectionProp {
    typeKey: BlockFormatType
}

export default function TextToolbar({ content }: { content: string }) {
    const [blockType, setBlockType] = useState<BlockFormatType>('paragraph');
    const [editor] = useLexicalComposerContext()
    const [activeEditor, setActiveEditor] = useState(editor);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const textFormatOptions = useMemo<TextToolbarGroupProp<TextFormatType>>(() => ({
        action: FORMAT_TEXT_COMMAND,
        options: [
            { icon: 'bold', option: 'bold', active: isBold },
            { icon: 'underline', option: 'underline', active: isUnderline },
            { icon: 'italic', option: 'italic', active: isItalic },
            { icon: 'strikethrough', option: 'strikethrough', active: isStrikethrough }
        ]
    }), [isBold, isItalic, isUnderline, isStrikethrough])
    const alignFormatOptions: TextToolbarGroupProp<ElementFormatType> = {
        action: FORMAT_ELEMENT_COMMAND,
        options: [
            { icon: 'align-left', option: 'left', active: false },
            { icon: 'align-right', option: 'right', active: false },
            { icon: 'align-center', option: 'center', active: false },
            { icon: 'align-justify', option: 'justify', active: false }
        ]
    }

    const formatHeading = () => {
        if (blockType !== 'h3') {
            editor.update(() => {
                const selection = $getSelection();
                if ($isRangeSelection(selection)) {
                    $setBlocksType(selection, () => $createHeadingNode('h3'));
                }
            });
        }
    };

    const formatList = useCallback((target: BlockFormatType, targetType: LexicalCommand<void>) => {
        if (blockType !== target) {
            activeEditor.dispatchCommand(targetType, undefined);
        } else {
            activeEditor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
        }
    }, [blockType, activeEditor])

    const blockFormatOptions: ToolbarBlockOptionProp[] = [
        { icon: 'heading', event: () => formatHeading(), typeKey: 'h3' },
        { icon: 'list-ul', event: () => formatList('bullet', INSERT_UNORDERED_LIST_COMMAND), typeKey: 'bullet' },
        { icon: 'list-ol', event: () => formatList('number', INSERT_ORDERED_LIST_COMMAND), typeKey: 'number' },
    ]
    const blockInsertOptions: ToolbarOptionSectionProp[] = [
        //{ icon: 'link', event: insertLink, }
    ]

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            let element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : $findMatchingParent(anchorNode, (e) => {
                        const parent = e.getParent();
                        return parent !== null && $isRootOrShadowRoot(parent);
                    });

            if (element === null) {
                element = anchorNode.getTopLevelElementOrThrow();
            }

            const elementKey = element.getKey();
            const elementDOM = activeEditor.getElementByKey(elementKey);

            setIsBold(selection.hasFormat("bold"));
            setIsItalic(selection.hasFormat("italic"));
            setIsUnderline(selection.hasFormat("underline"));
            setIsStrikethrough(selection.hasFormat("strikethrough"));
            if (elementDOM !== null) {
                if ($isListNode(element)) {
                    const parentList = $getNearestNodeOfType<ListNode>(
                        anchorNode,
                        ListNode,
                    );
                    const type = parentList
                        ? parentList.getListType()
                        : element.getListType();
                    setBlockType(type as BlockFormatType);
                } else {
                    const type = ($isHeadingNode(element)
                        ? element.getTag()
                        : element.getType()) as BlockFormatType
                    if (BlockFormatValues.includes(type)) {
                        setBlockType(type);
                    }
                }
            }
        }
    }, [activeEditor]);

    useEffect(() => {
        return editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            (_payload, newEditor) => {
                $updateToolbar();
                setActiveEditor(newEditor);
                return false;
            },
            COMMAND_PRIORITY_CRITICAL,
        );
    }, [editor, $updateToolbar]);

    useEffect(() => {
        return mergeRegister(
            activeEditor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            })
        );
    }, [$updateToolbar, activeEditor, editor])

    useEffect(() => {
        if (content) {
            editor.update(() => {
                const parser = new DOMParser();
                const dom = parser.parseFromString(content, 'text/html');
                const nodes = $generateNodesFromDOM(editor, dom);

                $getRoot().select();
                $insertNodes(nodes);
            })
        }
    }, [content])

    return (
        <Grid container className='texteditor-toolbar' justifyContent={'space-evenly'}>
            <ToolbarFormatSection editor={editor} group={textFormatOptions} />
            <ToolbarFormatSection editor={editor} group={alignFormatOptions} />
            <ToolbarSectionWrapper>
                {blockFormatOptions.map((o, idx) => <Button
                    key={idx}
                    sx={blockType !== o.typeKey ? { background: 'transparent' } : {}}
                    onClick={o.event}
                    className={blockType === o.typeKey ? 'app-btn-color' : ''}
                >
                    <FontAwesomeIcon icon={o.icon} />
                </Button>)}
            </ToolbarSectionWrapper>
            <ToolbarSectionWrapper>
                {blockInsertOptions.map((o, idx) => <Button
                    key={idx}
                    sx={{ background: 'transparent' }}
                    onClick={o.event}
                >
                    <FontAwesomeIcon icon={o.icon} />
                </Button>)}
            </ToolbarSectionWrapper>
        </Grid>
    )
}

function ToolbarFormatSection({ group, editor }: ToolbarFormatSectionProp) {
    return <ToolbarSectionWrapper>
        {group.options.map((o, idx) => <Button
            key={idx}
            sx={o.active ? {} : { background: 'transparent' }}
            onClick={() => editor.dispatchCommand(group.action, o.option)}
            className={o.active ? 'app-btn-color' : ''}
        >
            <FontAwesomeIcon icon={o.icon} />
        </Button>)}
    </ToolbarSectionWrapper>
}


function ToolbarSectionWrapper({ children }: { children: React.ReactNode }) {
    return <Grid item sx={{ margin: '0 0.5rem' }}>
        <ButtonGroup variant="contained" disableElevation size='large' color='inherit'>
            {children}
        </ButtonGroup>
    </Grid>
}
